import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarBlank,
  CheckCircle,
  Clock,
  GearSix,
  ListChecks,
  Plus,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  adjustStaffTimeOffBalance,
  cancelStaffTimeOffRequest,
  decideStaffTimeOffRequest,
  formatTimeOffAmount,
  getStaffTimeOffAdminSnapshot,
  timeOffStatusLabel,
  upsertStaffTimeOffPolicy,
} from "@/lib/scheduling";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Admin · /admin/time-off — desktop-first operational review for center-owned
 * time-off categories, human approvals, and the append-only balance ledger.
 */
export default function AdminTimeOff() {
  const { profile } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requestEditor, setRequestEditor] = useState(null);
  const [policyEditor, setPolicyEditor] = useState(null);
  const [balanceEditor, setBalanceEditor] = useState(false);
  const liveCenterUnavailable = !profile?.organization_id;

  const refresh = useCallback(async () => {
    if (!profile?.organization_id) {
      setSnapshot(null);
      setError("Time off requires a live HoneyAcorn center.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const from = startOfLocalDay(new Date()).toISOString();
      const to = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();
      setSnapshot(await getStaffTimeOffAdminSnapshot({ from, to }));
    } catch (err) {
      setError(err?.message || "Could not load the time-off workspace.");
    } finally {
      setLoading(false);
    }
  }, [profile?.organization_id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const requests = useMemo(
    () => [...(snapshot?.requests || [])].sort((left, right) => {
      const priority = { pending: 0, approved: 1, declined: 2, cancelled: 3 };
      const difference = (priority[left.status] ?? 4) - (priority[right.status] ?? 4);
      if (difference) return difference;
      return new Date(left.startsAt || 0).getTime() - new Date(right.startsAt || 0).getTime();
    }),
    [snapshot?.requests],
  );
  const pending = requests.filter((request) => request.status === "pending");
  const upcomingApproved = requests.filter((request) => request.status === "approved" && new Date(request.endsAt) >= new Date());
  const conflicts = requests.filter((request) => request.status !== "cancelled" && request.scheduledShiftConflictCount > 0);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <div data-testid="admin-time-off-unavailable" className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-6">
        <WarningCircle size={24} weight="duotone" className="text-neutral-500" />
        <h1 className="mt-3 font-heading text-xl font-semibold text-neutral-900">
          {liveCenterUnavailable ? "Time off needs a live center" : "Time off is not ready yet"}
        </h1>
        <p className="mt-2 max-w-xl text-sm text-neutral-600">
          {liveCenterUnavailable
            ? "This local preview is not connected to a HoneyAcorn center. Scheduling remains available to preview."
            : "The existing scheduling workspace is still available while this center’s update becomes active."}
        </p>
        {!liveCenterUnavailable && (
          <button type="button" onClick={refresh} className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover">Try again</button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="admin-time-off-page" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Staffing</p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">Time off</h1>
          <p className="mt-2 max-w-3xl text-sm text-neutral-600">
            Review availability requests and keep center-owned balances clear. HoneyAcorn does not calculate accruals, eligibility, payroll, or paid leave.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-time-off-add-policy"
          onClick={() => setPolicyEditor({})}
          className="inline-flex min-h-10 items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
        >
          <Plus size={17} weight="bold" />
          Add category
        </button>
      </header>

      <section data-testid="admin-time-off-summary" className="grid gap-3 sm:grid-cols-3">
        <StatCard icon={Clock} label="Awaiting review" value={pending.length} tone={pending.length ? "warn" : "muted"} />
        <StatCard icon={CheckCircle} label="Upcoming approved" value={upcomingApproved.length} tone={upcomingApproved.length ? "ok" : "muted"} />
        <StatCard icon={WarningCircle} label="Schedule review" value={conflicts.length} tone={conflicts.length ? "warn" : "muted"} />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-semibold text-neutral-900">Requests</h2>
            <p className="mt-1 text-sm text-neutral-600">Pending requests stay visible first. Approved time off only flags planned coverage; it never changes a shift automatically.</p>
          </div>
          <Link to="/admin/scheduling" className="inline-flex min-h-9 items-center rounded-full border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">
            Open scheduling
          </Link>
        </div>
        <RequestList requests={requests} onReview={setRequestEditor} />
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <PoliciesPanel policies={snapshot?.policies || []} onEdit={setPolicyEditor} />
        <BalancePanel
          staff={snapshot?.staff || []}
          policies={(snapshot?.policies || []).filter((policy) => policy.isActive && policy.tracksBalance)}
          balances={snapshot?.balances || []}
          onAdjust={() => setBalanceEditor(true)}
        />
      </section>

      <RequestDecisionDialog
        request={requestEditor}
        onClose={() => setRequestEditor(null)}
        onChanged={async () => {
          setRequestEditor(null);
          await refresh();
        }}
      />
      <PolicyDialog
        policy={policyEditor}
        onClose={() => setPolicyEditor(null)}
        onChanged={async () => {
          setPolicyEditor(null);
          await refresh();
        }}
      />
      <BalanceDialog
        open={balanceEditor}
        staff={snapshot?.staff || []}
        policies={(snapshot?.policies || []).filter((policy) => policy.isActive && policy.tracksBalance)}
        balances={snapshot?.balances || []}
        onClose={() => setBalanceEditor(false)}
        onChanged={async () => {
          setBalanceEditor(false);
          await refresh();
        }}
      />
    </div>
  );
}

function LoadingState() {
  return (
    <div data-testid="admin-time-off-loading" className="space-y-5">
      <div className="h-8 w-48 animate-pulse rounded bg-neutral-100" />
      <div className="grid gap-3 sm:grid-cols-3">{[0, 1, 2].map((item) => <div key={item} className="h-24 animate-pulse rounded-2xl border border-neutral-100 bg-white" />)}</div>
      <div className="h-80 animate-pulse rounded-2xl border border-neutral-100 bg-white" />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  const styles = {
    ok: "border-primary/20 bg-primary/5 text-primary-hover",
    warn: "border-[#C6851E]/20 bg-[#C6851E]/5 text-[#8A5A12]",
    muted: "border-neutral-200 bg-white text-neutral-500",
  }[tone] || "border-neutral-200 bg-white text-neutral-500";
  return (
    <div className={`rounded-2xl border p-4 ${styles}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-[0.14em]">{label}</p>
        <Icon size={19} weight="duotone" />
      </div>
      <p className="mt-3 font-heading text-3xl font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function RequestList({ requests, onReview }) {
  if (requests.length === 0) {
    return (
      <div data-testid="admin-time-off-requests-empty" className="mt-5 rounded-xl border border-dashed border-neutral-200 bg-neutral-50 px-4 py-8 text-center">
        <CalendarBlank size={26} weight="duotone" className="mx-auto text-neutral-400" />
        <p className="mt-3 text-sm font-semibold text-neutral-900">No time-off requests in this review window</p>
        <p className="mt-1 text-xs text-neutral-500">New teacher requests will appear here, ready for a human decision.</p>
      </div>
    );
  }

  return (
    <ol data-testid="admin-time-off-requests" className="mt-5 divide-y divide-neutral-100 rounded-xl border border-neutral-100">
      {requests.map((request) => (
        <li key={request.id} data-testid={`admin-time-off-request-${request.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-3 p-4">
          <div className="min-w-[180px] flex-1">
            <p className="text-sm font-semibold text-neutral-900">{request.profileName}</p>
            <p className="mt-0.5 text-xs text-neutral-500">{request.policyLabel} · {formatDateRange(request.startsAt, request.endsAt)}</p>
          </div>
          <p className="text-sm font-medium text-neutral-700">{formatTimeOffAmount(request.requestedAmount, request.balanceUnit)}</p>
          <StatusChip status={request.status} />
          {request.scheduledShiftConflictCount > 0 && request.status !== "cancelled" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#C6851E]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8A5A12]">
              <WarningCircle size={12} weight="duotone" />
              {request.scheduledShiftConflictCount} shift{request.scheduledShiftConflictCount === 1 ? "" : "s"}
            </span>
          )}
          <button
            type="button"
            data-testid={`admin-time-off-review-${request.id}`}
            onClick={() => onReview(request)}
            className="ml-auto inline-flex min-h-9 items-center rounded-full border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            {request.status === "pending" ? "Review" : "View"}
          </button>
        </li>
      ))}
    </ol>
  );
}

function PoliciesPanel({ policies, onEdit }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-primary/10 p-2 text-primary-hover"><GearSix size={19} weight="duotone" /></span>
        <div>
          <h2 className="font-heading text-lg font-semibold text-neutral-900">Center categories</h2>
          <p className="mt-1 text-sm text-neutral-600">Configure the categories, units, and whether your center wants to track a manual balance.</p>
        </div>
      </div>
      {policies.length === 0 ? (
        <div data-testid="admin-time-off-policies-empty" className="mt-5 rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">
          No categories yet. Add only the policies your center has approved.
        </div>
      ) : (
        <ul className="mt-5 space-y-2">
          {policies.map((policy) => (
            <li key={policy.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-100 bg-neutral-50 p-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-neutral-900">{policy.label}</p>
                <p className="mt-0.5 text-xs text-neutral-500">{policy.tracksBalance ? `Manual ${policy.balanceUnit} balance` : "No balance tracking"}{policy.isActive ? "" : " · Inactive"}</p>
              </div>
              <button type="button" data-testid={`admin-time-off-edit-policy-${policy.id}`} onClick={() => onEdit(policy)} className="min-h-9 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100">Edit</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function BalancePanel({ staff, policies, balances, onAdjust }) {
  const configured = balances.filter((balance) => balance.balanceEntryCount > 0);
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-3">
          <span className="rounded-xl bg-primary/10 p-2 text-primary-hover"><ListChecks size={19} weight="duotone" /></span>
          <div>
            <h2 className="font-heading text-lg font-semibold text-neutral-900">Manual balances</h2>
            <p className="mt-1 text-sm text-neutral-600">Opening and adjustments are logged. They inform review but never make the approval decision automatically.</p>
          </div>
        </div>
        <button type="button" data-testid="admin-time-off-adjust-balance" onClick={onAdjust} disabled={staff.length === 0 || policies.length === 0} className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"><Plus size={14} weight="bold" />Adjust</button>
      </div>
      {policies.length === 0 ? (
        <p className="mt-5 rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">Add an active category with balance tracking before recording a balance.</p>
      ) : configured.length === 0 ? (
        <p data-testid="admin-time-off-balances-empty" className="mt-5 rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-600">No balances recorded. Set only the balances your center has approved.</p>
      ) : (
        <ul data-testid="admin-time-off-balances" className="mt-5 space-y-2">
          {configured.slice(0, 8).map((balance) => {
            const person = staff.find((candidate) => candidate.id === balance.profileId);
            return (
              <li key={`${balance.profileId}-${balance.policyId}`} className="flex items-center justify-between gap-3 rounded-xl bg-neutral-50 px-3 py-2.5">
                <div className="min-w-0"><p className="truncate text-sm font-semibold text-neutral-900">{person?.fullName || "Staff member"}</p><p className="text-xs text-neutral-500">{balance.policyLabel}</p></div>
                <p className="shrink-0 text-sm font-semibold text-neutral-800">{formatTimeOffAmount(balance.balanceAmount, balance.balanceUnit)}</p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function RequestDecisionDialog({ request, onClose, onChanged }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setNote("");
  }, [request?.id]);

  async function decide(decision) {
    if (!request?.id) return;
    if (decision === "declined" && !note.trim()) {
      toast.error("Add a reason before declining this request.");
      return;
    }
    setBusy(true);
    try {
      await decideStaffTimeOffRequest({ requestId: request.id, decision, decisionNote: note.trim() || null });
      toast.success(decision === "approved" ? "Time off approved" : "Time off declined");
      await onChanged?.();
    } catch (err) {
      toast.error(err?.message || "Could not update this request");
    } finally {
      setBusy(false);
    }
  }

  async function cancelApproved() {
    if (!request?.id) return;
    if (!note.trim()) {
      toast.error("Add a cancellation reason before continuing.");
      return;
    }
    setBusy(true);
    try {
      await cancelStaffTimeOffRequest({ requestId: request.id, cancelNote: note.trim() });
      toast.success("Time-off request cancelled");
      await onChanged?.();
    } catch (err) {
      toast.error(err?.message || "Could not cancel this request");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!request} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent data-testid="admin-time-off-request-dialog" className="max-h-[88dvh] overflow-y-auto sm:max-w-lg">
        {request && <>
          <DialogHeader><DialogTitle>Review time off</DialogTitle><DialogDescription>Keep the decision clear for the staff member. Approval does not alter planned shifts automatically.</DialogDescription></DialogHeader>
          <div className="mt-4 rounded-xl bg-neutral-50 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3"><div><p className="font-semibold text-neutral-900">{request.profileName}</p><p className="mt-1 text-sm text-neutral-600">{request.policyLabel} · {formatTimeOffAmount(request.requestedAmount, request.balanceUnit)}</p></div><StatusChip status={request.status} /></div>
            <p className="mt-3 text-sm text-neutral-700">{formatDateRange(request.startsAt, request.endsAt)}</p>
            {request.requestNote && <p className="mt-3 rounded-lg bg-white px-3 py-2 text-sm leading-6 text-neutral-700">{request.requestNote}</p>}
            {request.scheduledShiftConflictCount > 0 && request.status !== "cancelled" && <p className="mt-3 flex items-start gap-2 text-sm text-[#8A5A12]"><WarningCircle size={17} weight="duotone" className="mt-0.5 shrink-0" />{request.scheduledShiftConflictCount} planned shift{request.scheduledShiftConflictCount === 1 ? "" : "s"} overlap. Review coverage in Scheduling after this decision.</p>}
          </div>
          {request.status === "pending" ? <>
            <label className="mt-4 block"><span className="text-sm font-semibold text-neutral-800">Decision note <span className="font-normal text-neutral-500">(required when declining)</span></span><textarea data-testid="admin-time-off-decision-note" value={note} onChange={(event) => setNote(event.target.value)} maxLength={2000} rows={4} className="mt-1.5 w-full resize-y rounded-xl border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
            <div className="mt-5 flex flex-wrap justify-end gap-2"><button type="button" disabled={busy} onClick={onClose} className="min-h-10 rounded-full border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Close</button><button type="button" data-testid="admin-time-off-decline" disabled={busy} onClick={() => decide("declined")} className="min-h-10 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60">Decline</button><button type="button" data-testid="admin-time-off-approve" disabled={busy} onClick={() => decide("approved")} className="min-h-10 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">Approve</button></div>
          </> : <>
            {(request.decisionNote || request.cancelNote) && <p className="mt-4 rounded-xl border border-neutral-100 bg-neutral-50 p-3 text-sm leading-6 text-neutral-700">{request.cancelNote || request.decisionNote}</p>}
            {request.status === "approved" && <label className="mt-4 block"><span className="text-sm font-semibold text-neutral-800">Cancellation reason</span><textarea data-testid="admin-time-off-cancel-note" value={note} onChange={(event) => setNote(event.target.value)} maxLength={2000} rows={3} className="mt-1.5 w-full resize-y rounded-xl border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>}
            <div className="mt-5 flex flex-wrap justify-end gap-2"><button type="button" disabled={busy} onClick={onClose} className="min-h-10 rounded-full border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Close</button>{request.status === "approved" && <button type="button" data-testid="admin-time-off-cancel-approved" disabled={busy} onClick={cancelApproved} className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"><X size={15} weight="bold" />Cancel approved request</button>}</div>
          </>}
        </>}
      </DialogContent>
    </Dialog>
  );
}

function PolicyDialog({ policy, onClose, onChanged }) {
  const [form, setForm] = useState(emptyPolicyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(policy ? {
      code: policy.code || "",
      label: policy.label || "",
      description: policy.description || "",
      balanceUnit: policy.balanceUnit || "hours",
      tracksBalance: !!policy.tracksBalance,
      isActive: policy.isActive !== false,
    } : emptyPolicyForm());
  }, [policy]);

  async function submit(event) {
    event.preventDefault();
    if (!form.code.trim() || !form.label.trim()) {
      toast.error("Add a policy key and a category name.");
      return;
    }
    setSaving(true);
    try {
      await upsertStaffTimeOffPolicy({
        policyId: policy?.id || null,
        code: form.code.trim().toLowerCase(),
        label: form.label.trim(),
        description: form.description.trim() || null,
        balanceUnit: form.balanceUnit,
        tracksBalance: form.tracksBalance,
        isActive: form.isActive,
      });
      toast.success(policy?.id ? "Time-off category updated" : "Time-off category added");
      await onChanged?.();
    } catch (err) {
      toast.error(err?.message || "Could not save this category");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={policy !== null} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogContent data-testid="admin-time-off-policy-dialog" className="max-h-[88dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>{policy?.id ? "Edit time-off category" : "Add time-off category"}</DialogTitle><DialogDescription>Use only center-approved language. HoneyAcorn does not provide default leave or legal-policy values.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2"><label className="block"><span className="text-sm font-semibold text-neutral-800">Category name</span><input data-testid="admin-time-off-policy-label" value={form.label} onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))} maxLength={120} className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" required /></label><label className="block"><span className="text-sm font-semibold text-neutral-800">Policy key</span><input data-testid="admin-time-off-policy-code" value={form.code} onChange={(event) => setForm((current) => ({ ...current, code: event.target.value.toLowerCase().replace(/\s+/g, "_") }))} maxLength={64} pattern="[a-z0-9][a-z0-9_-]*" className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 px-3 font-mono text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" required /></label></div>
          <label className="block"><span className="text-sm font-semibold text-neutral-800">Center note <span className="font-normal text-neutral-500">(optional)</span></span><textarea data-testid="admin-time-off-policy-description" value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} maxLength={1000} rows={3} className="mt-1.5 w-full resize-y rounded-xl border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" /></label>
          <div className="rounded-xl border border-neutral-200 p-3"><div className="flex flex-wrap items-center justify-between gap-3"><div><p className="text-sm font-semibold text-neutral-900">Track a manual balance</p><p className="mt-1 text-xs leading-5 text-neutral-600">Balances are center-recorded adjustments. They inform review but never approve or decline automatically.</p></div><input data-testid="admin-time-off-policy-tracks-balance" type="checkbox" checked={form.tracksBalance} onChange={(event) => setForm((current) => ({ ...current, tracksBalance: event.target.checked }))} className="h-5 w-5 accent-primary" /></div>{form.tracksBalance && <label className="mt-3 block"><span className="text-sm font-semibold text-neutral-800">Balance unit</span><select data-testid="admin-time-off-policy-unit" value={form.balanceUnit} onChange={(event) => setForm((current) => ({ ...current, balanceUnit: event.target.value }))} className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"><option value="hours">Hours</option><option value="days">Days</option></select></label>}</div>
          <label className="flex items-center gap-2 text-sm font-medium text-neutral-800"><input data-testid="admin-time-off-policy-active" type="checkbox" checked={form.isActive} onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))} className="h-4 w-4 accent-primary" />Available for new requests</label>
          <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="min-h-10 rounded-full border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Cancel</button><button data-testid="admin-time-off-policy-save" type="submit" disabled={saving} className="min-h-10 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">{saving ? "Saving…" : "Save category"}</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BalanceDialog({ open, staff, policies, balances, onClose, onChanged }) {
  const [profileId, setProfileId] = useState("");
  const [policyId, setPolicyId] = useState("");
  const [amount, setAmount] = useState("");
  const [effectiveOn, setEffectiveOn] = useState(localDateInput());
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const selectedPolicy = policies.find((policy) => policy.id === policyId) || policies[0] || null;
  const selectedBalance = balances.find((balance) => balance.profileId === profileId && balance.policyId === selectedPolicy?.id) || null;

  useEffect(() => {
    if (!open) return;
    setProfileId((current) => current || staff[0]?.id || "");
    setPolicyId((current) => current || policies[0]?.id || "");
    setAmount("");
    setReason("");
    setEffectiveOn(localDateInput());
  }, [open, staff, policies]);

  async function submit(event) {
    event.preventDefault();
    const numericAmount = Number(amount);
    if (!profileId || !selectedPolicy || !Number.isFinite(numericAmount) || numericAmount === 0 || !reason.trim()) {
      toast.error("Choose a teacher and category, then enter a non-zero adjustment and reason.");
      return;
    }
    setSaving(true);
    try {
      await adjustStaffTimeOffBalance({ profileId, policyId: selectedPolicy.id, amount: numericAmount, effectiveOn, reason: reason.trim() });
      toast.success("Time-off balance adjusted");
      await onChanged?.();
    } catch (err) {
      toast.error(err?.message || "Could not adjust this balance");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose?.(); }}>
      <DialogContent data-testid="admin-time-off-balance-dialog" className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Adjust a time-off balance</DialogTitle><DialogDescription>This creates an append-only center record. Use a positive or negative amount and document why.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2"><label className="block"><span className="text-sm font-semibold text-neutral-800">Staff member</span><select data-testid="admin-time-off-balance-staff" value={profileId} onChange={(event) => setProfileId(event.target.value)} className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" required>{staff.map((person) => <option key={person.id} value={person.id}>{person.fullName}</option>)}</select></label><label className="block"><span className="text-sm font-semibold text-neutral-800">Category</span><select data-testid="admin-time-off-balance-policy" value={selectedPolicy?.id || ""} onChange={(event) => setPolicyId(event.target.value)} className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" required>{policies.map((policy) => <option key={policy.id} value={policy.id}>{policy.label}</option>)}</select></label></div>
          {selectedPolicy && <div className="rounded-xl bg-primary/5 px-3 py-2 text-sm text-neutral-700">Current recorded balance: <span className="font-semibold text-neutral-900">{selectedBalance?.balanceEntryCount ? formatTimeOffAmount(selectedBalance.balanceAmount, selectedPolicy.balanceUnit) : "Not recorded"}</span></div>}
          <div className="grid gap-3 sm:grid-cols-2"><label className="block"><span className="text-sm font-semibold text-neutral-800">Adjustment ({selectedPolicy?.balanceUnit || "hours"})</span><input data-testid="admin-time-off-balance-amount" type="number" step="0.25" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="e.g. 8 or -2" className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" required /></label><label className="block"><span className="text-sm font-semibold text-neutral-800">Effective date</span><input data-testid="admin-time-off-balance-effective-on" type="date" value={effectiveOn} onChange={(event) => setEffectiveOn(event.target.value)} className="mt-1.5 min-h-11 w-full rounded-xl border border-neutral-200 px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" required /></label></div>
          <label className="block"><span className="text-sm font-semibold text-neutral-800">Reason</span><textarea data-testid="admin-time-off-balance-reason" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={1000} rows={3} className="mt-1.5 w-full resize-y rounded-xl border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" required /></label>
          <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="min-h-10 rounded-full border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Cancel</button><button data-testid="admin-time-off-balance-save" type="submit" disabled={saving} className="min-h-10 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">{saving ? "Saving…" : "Save adjustment"}</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatusChip({ status }) {
  const tone = {
    approved: "bg-primary/10 text-primary-hover",
    declined: "bg-red-50 text-red-700",
    cancelled: "bg-neutral-200 text-neutral-600",
    pending: "bg-[#C6851E]/10 text-[#8A5A12]",
  }[status] || "bg-neutral-100 text-neutral-600";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone}`}>{timeOffStatusLabel(status)}</span>;
}

function emptyPolicyForm() {
  return { code: "", label: "", description: "", balanceUnit: "hours", tracksBalance: false, isActive: true };
}

function formatDateRange(startsAt, endsAt) {
  if (!startsAt || !endsAt) return "Dates to be confirmed";
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const date = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });
  const time = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
  if (start.toDateString() === end.toDateString()) return `${date.format(start)} · ${time.format(start)}–${time.format(end)}`;
  return `${date.format(start)} ${time.format(start)} – ${date.format(end)} ${time.format(end)}`;
}

function localDateInput(date = new Date()) {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function startOfLocalDay(date) {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}
